import React, { useState, useEffect } from 'react';
import './CourseDetailsModal.css';
import { insertPotentialCourse, customizeSyllabus } from '../services/api';
import { useNotification } from './Notification';
import CustomDropdown from './CustomDropdown';

const CourseDetailsModal = ({ 
  isOpen, 
  onClose, 
  selectedCourse, 
  selectedCategory,
  programName,
  recommendedCoursesData,
  onSuccess,
  type = 'course' // 'course' or 'module'
}) => {
  const { addNotification } = useNotification();
  const [isAddingCourse, setIsAddingCourse] = useState(false);
  const [formData, setFormData] = useState({
    courseCode: type === 'module' && selectedCourse ? selectedCourse.course_title : '',
    credits: '',
    semester: type === 'course' ? '' : undefined,
    addInstructor: false,
    addSchedule: false,
    instructorName: '',
    instructorEmail: '',
    courseTimeStart: '09:00 AM',
    courseTimeEnd: '12:00 PM',
    courseDays: ['Tuesday', 'Wednesday'], // Selected days
    officeHoursStart: '09:00 AM',
    officeHoursEnd: '12:00 PM',
    officeHoursDays: ['Tuesday', 'Wednesday'] // Selected days
  });

  // Update course code when selectedCourse changes for modules
  useEffect(() => {
    if (type === 'module' && selectedCourse && selectedCourse.course_title) {
      setFormData(prev => ({
        ...prev,
        courseCode: selectedCourse.course_title
      }));
    }
  }, [selectedCourse, type]);

  if (!isOpen) return null;

  // Generate semester options for the next 5 years
  const generateSemesterOptions = () => {
    const currentYear = new Date().getFullYear();
    const semesters = ['Fall', 'Spring', 'Summer'];
    const options = [];
    
    for (let year = currentYear; year <= currentYear + 5; year++) {
      semesters.forEach(semester => {
        options.push({
          value: `${semester} ${year}`,
          label: `${semester} ${year}`
        });
      });
    }
    
    return options;
  };

  // Generate time options for time picker
  const generateTimeOptions = () => {
    const times = [];
    const periods = ['AM', 'PM'];
    
    periods.forEach(period => {
      for (let hour = 1; hour <= 12; hour++) {
        for (let minute = 0; minute < 60; minute += 30) {
          const hourStr = hour.toString();
          const minuteStr = minute.toString().padStart(2, '0');
          const timeStr = `${hourStr}:${minuteStr} ${period}`;
          times.push({
            value: timeStr,
            label: timeStr
          });
        }
      }
    });
    
    return times;
  };

  // Handle day selection
  const handleDayToggle = (field, day) => {
    setFormData(prev => {
      const currentDays = prev[field];
      const newDays = currentDays.includes(day)
        ? currentDays.filter(d => d !== day)
        : [...currentDays, day];
      
      return {
        ...prev,
        [field]: newDays
      };
    });
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCheckboxChange = (field) => {
    setFormData(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleCancel = () => {
    setFormData({
      courseCode: type === 'module' && selectedCourse ? selectedCourse.course_title : '',
      credits: '',
      semester: type === 'course' ? '' : undefined,
      addInstructor: false,
      addSchedule: false,
      instructorName: '',
      instructorEmail: '',
      courseTimeStart: '09:00 AM',
      courseTimeEnd: '12:00 PM',
      courseDays: ['Tuesday', 'Wednesday'],
      officeHoursStart: '09:00 AM',
      officeHoursEnd: '12:00 PM',
      officeHoursDays: ['Tuesday', 'Wednesday']
    });
    onClose();
  };

  const handleBack = () => {
    // Go back to the previous modal
    onClose();
  };

  const handleAddCourseToProgram = async () => {
    if (!selectedCourse) {
      addNotification('No course selected', 'warning');
      return;
    }

    if (isAddingCourse) {
      return; // Prevent multiple simultaneous requests
    }

    setIsAddingCourse(true);

    // Map category to course_type (use exact checkbox labels)
    const categoryToCourseType = {
      'general-education': 'General Education',
      'electives': 'Electives',
      'major-requirements': 'Major Requirement',
      'module': 'Module'
    };

    const courseType = categoryToCourseType[selectedCategory] || (type === 'module' ? 'Module' : 'General Education');
    
    try {
      // Get course title from the selected course
      const courseTitle = selectedCourse.course_title || (type === 'module' ? 'General Module' : 'General Course');
      const courseID = selectedCourse.id || selectedCourse.program_job_id;

      // Check if we have a valid ID
      if (!courseID) {
        addNotification(`No ID found for ${type === 'module' ? 'module' : 'course'}. Cannot proceed.`, 'error');
        return;
      }
      
      console.log(`Adding ${type}:`, {
        courseTitle,
        courseID,
        courseType,
        category: selectedCategory,
        formData,
        selectedCourse // Add this to see what's in selectedCourse
      });

      // Step 1: Insert potential course
      const result = await insertPotentialCourse(courseID, courseType);
      console.log(`${type} added successfully:`, result);
      
      // Step 2: Customize syllabus by finding course data from recommendedCoursesData
      console.log('Looking for course in recommendedCoursesData:', {
        courseTitle,
        courseID,
        hasRecommendedData: !!recommendedCoursesData,
        coursesCount: recommendedCoursesData?.courses?.length || 0
      });
      
      if (recommendedCoursesData && recommendedCoursesData.courses) {
        // Find the matching course by course_title
        const matchingCourse = recommendedCoursesData.courses.find(course => 
          course.id === courseID || course.program_job_id === courseID
        );
        
        console.log('Matching course found:', matchingCourse);
        
        if (matchingCourse && matchingCourse.id && matchingCourse.markdown_s3_url) {
          console.log('Customizing syllabus with:', {
            id: matchingCourse.id,
            markdown_s3_url: matchingCourse.markdown_s3_url
          });
          
          // Transform form data for customize-syllabus endpoint
          const syllabusData = {
            syllabus_s3_url: matchingCourse.markdown_s3_url, // Use markdown_s3_url as syllabus_s3_url
            id: matchingCourse.id,
            course_code: type === 'module' ? null : (formData.courseCode || ''),
            course_name: type === 'module' ? formData.courseCode : undefined,
            credits: formData.credits || '',
            semester: formData.semester || '',
          };

        // Add instructor details if provided
        if (formData.addInstructor && (formData.instructorName || formData.instructorEmail)) {
          if (formData.instructorName) {
            syllabusData.instructor_name = formData.instructorName;
          }
          if (formData.instructorEmail) {
            syllabusData.instructor_email = formData.instructorEmail;
          }
        }

        // Add schedule details if provided
        if (formData.addSchedule) {
          // Format meeting times without line break
          if (formData.courseTimeStart && formData.courseTimeEnd && formData.courseDays.length > 0) {
            const daysString = formData.courseDays.join(', ');
            syllabusData.meeting_times = `${daysString} ${formData.courseTimeStart} – ${formData.courseTimeEnd}`;
          }

          // Format office hours without line break
          if (formData.officeHoursStart && formData.officeHoursEnd && formData.officeHoursDays.length > 0) {
            const officeDaysString = formData.officeHoursDays.join(', ');
            syllabusData.office_hours = `${officeDaysString} ${formData.officeHoursStart} – ${formData.officeHoursEnd}`;
          }
        }

        console.log('Syllabus customization data:', syllabusData);

          try {
            console.log('Starting customize-syllabus API call...');
            const syllabusResult = await customizeSyllabus(syllabusData);
            console.log('Syllabus customized successfully:', syllabusResult);
            console.log('Syllabus result type:', typeof syllabusResult);
            console.log('Syllabus result keys:', syllabusResult ? Object.keys(syllabusResult) : 'null/undefined');
            
            // Show success notification with both operations
            addNotification(`${type === 'module' ? 'Module' : 'Course'} "${courseTitle}" has been successfully added and syllabus customized!`, 'success');
          } catch (syllabusError) {
            console.error('Error customizing syllabus:', syllabusError);
            console.error('Syllabus error details:', syllabusError.message);
            // Show partial success notification
            addNotification(`${type === 'module' ? 'Module' : 'Course'} "${courseTitle}" has been added, but syllabus customization failed.`, 'warning');
          }
        } else {
          // Show success notification for course addition only
          addNotification(`${type === 'module' ? 'Module' : 'Course'} "${courseTitle}" has been successfully added!`, 'success');
          console.log('Syllabus customization skipped - missing course data, id, or markdown_s3_url');
        }
      } else {
        // Show success notification for course addition only
        addNotification(`${type === 'module' ? 'Module' : 'Course'} "${courseTitle}" has been successfully added!`, 'success');
        console.log('Syllabus customization skipped - no recommendedCoursesData available');
      }
      
      // Close modal and reset state after successful completion
      handleCancel();
      
      // Call success callback if provided (this will trigger get-recommended-course-details refresh)
      console.log('About to call onSuccess callback...');
      console.log('onSuccess callback exists:', !!onSuccess);
      console.log('onSuccess callback type:', typeof onSuccess);
      
      if (onSuccess) {
        try {
          console.log('Calling onSuccess callback...');
          const result = onSuccess();
          console.log('onSuccess callback result:', result);
          
          // If it returns a promise, wait for it
          if (result && typeof result.then === 'function') {
            console.log('onSuccess returned a promise, waiting...');
            await result;
            console.log('onSuccess promise resolved');
          }
        } catch (callbackError) {
          console.error('Error in onSuccess callback:', callbackError);
        }
      } else {
        console.log('No onSuccess callback provided');
      }
      
    } catch (error) {
      console.error(`Error adding ${type}:`, error);
      addNotification(`Failed to add ${type === 'module' ? 'module' : 'course'}. Please try again.`, 'error');
    } finally {
      setIsAddingCourse(false);
    }
  };

  return (
    <div className="course-details-modal-overlay" onClick={handleCancel}>
      <div className="course-details-modal" onClick={(e) => e.stopPropagation()}>
        <button className="course-details-close-btn" onClick={handleCancel}>
          <svg  viewBox="0 0 24 24" fill="none">
            <path d="M18 6L6 18M6 6L18 18" stroke="#000000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        
        <div className="course-details-content">
          <div className="course-details-header">
            <h2 className="course-details-title">
              Add {type === 'module' ? 'Module' : 'Course'} to Program
            </h2>
            <p className="course-details-subtitle">
              Please fill out the following {type === 'module' ? 'module' : 'course'} details as needed.
            </p>
          </div>

          {/* Basic Information Section */}
          <div className="course-details-section">
            <div className="course-details-section-header">
              <h3>Basic Information (Optional)</h3>
            </div>
            <div className="form-fields-row">
              <div className="form-field">
                <label className="field-label">
                  {type === 'module' ? 'Module Name' : 'Course Code'}
                </label>
                <input
                  type="text"
                  className="field-input"
                  placeholder={type === 'module' ? 'e.g. Leadership and Team Management' : 'e.g. MGMT 101'}
                  value={formData.courseCode}
                  onChange={(e) => handleInputChange('courseCode', e.target.value)}
                />
              </div>
              <div className="form-field">
                <label className="field-label">Credits</label>
                <input
                  type="text"
                  className="field-input"
                  placeholder="e.g. 3"
                  value={formData.credits}
                  onChange={(e) => handleInputChange('credits', e.target.value)}
                />
              </div>
              {type === 'course' && (
                <div className="form-field">
                  <label className="field-label">Semester</label>
                  <CustomDropdown
                    options={generateSemesterOptions()}
                    value={formData.semester}
                    onChange={(value) => handleInputChange('semester', value)}
                    placeholder="Select Semester"
                    className="semester-dropdown"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Instructor Information Section */}
          <div className="course-details-section instructor-section">
            <div className="course-details-section-header">
              <h3>Instructor Information (Optional)</h3>
            </div>
            <div className="checkbox-field">
              <label className={`credential-option ${formData.addInstructor ? 'selected' : ''}`}>
                <input
                  type="checkbox"
                  name="addInstructor"
                  checked={formData.addInstructor}
                  onChange={() => handleCheckboxChange('addInstructor')}
                />
                <span className="credential-text">Add instructor details</span>
              </label>
            </div>
            
            {/* Expandable Instructor Fields */}
            {formData.addInstructor && (
              <div className="instructor-fields">
                <div className="instructor-fields-row">
                  <div className="form-field">
                    <label className="field-label">Instructor Name (Optional)</label>
                    <input
                      type="text"
                      className="field-input"
                      placeholder="e.g. Dr. Jane Smith"
                      value={formData.instructorName}
                      onChange={(e) => handleInputChange('instructorName', e.target.value)}
                    />
                  </div>
                  <div className="form-field">
                    <label className="field-label">Instructor Email (Optional)</label>
                    <input
                      type="email"
                      className="field-input"
                      placeholder="jane.smith@university.edu"
                      value={formData.instructorEmail}
                      onChange={(e) => handleInputChange('instructorEmail', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Schedule Section */}
          <div className="course-details-section schedule-section">
            <div className="course-details-section-header">
              <h3>{type === 'module' ? 'Module' : 'Course'} Schedule (Optional)</h3>
            </div>
            <div className="checkbox-field">
              <label className={`credential-option ${formData.addSchedule ? 'selected' : ''}`}>
                <input
                  type="checkbox"
                  name="addSchedule"
                  checked={formData.addSchedule}
                  onChange={() => handleCheckboxChange('addSchedule')}
                />
                <span className="credential-text">Add {type === 'module' ? 'module' : 'course'} schedule</span>
              </label>
            </div>
            
            {/* Expandable Schedule Fields */}
            {formData.addSchedule && (
              <div className="schedule-fields">
                {/* Time Section */}
                <div className="schedule-time-section">
                  <label className="field-label">{type === 'module' ? 'Module' : 'Course'} Time (Optional)</label>
                  <div className="time-picker-row">
                    <CustomDropdown
                      options={generateTimeOptions()}
                      value={formData.courseTimeStart}
                      onChange={(value) => handleInputChange('courseTimeStart', value)}
                      placeholder="09:00 AM"
                      className="time-dropdown"
                    />
                    <span className="time-separator">to</span>
                    <CustomDropdown
                      options={generateTimeOptions()}
                      value={formData.courseTimeEnd}
                      onChange={(value) => handleInputChange('courseTimeEnd', value)}
                      placeholder="12:00 PM"
                      className="time-dropdown"
                    />
                  </div>
                  
                  {/* Days */}
                  <div className="days-selector">
                    {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'].map(day => (
                      <button
                        key={day}
                        type="button"
                        className={`day-button ${formData.courseDays.includes(day) ? 'day-button-selected' : ''}`}
                        onClick={() => handleDayToggle('courseDays', day)}
                      >
                        {formData.courseDays.includes(day) && (
                          <svg className="check-icon"  viewBox="0 0 24 24" fill="none">
                            <path d="M9 12L11 14L15 10" stroke="#000000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        )}
                        <span>{day.slice(0, 3)}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Office Hours Section */}
                <div className="schedule-time-section">
                  <label className="field-label">Office Hours (Optional)</label>
                  <div className="time-picker-row">
                    <CustomDropdown
                      options={generateTimeOptions()}
                      value={formData.officeHoursStart}
                      onChange={(value) => handleInputChange('officeHoursStart', value)}
                      placeholder="09:00 AM"
                      className="time-dropdown"
                    />
                    <span className="time-separator">to</span>
                    <CustomDropdown
                      options={generateTimeOptions()}
                      value={formData.officeHoursEnd}
                      onChange={(value) => handleInputChange('officeHoursEnd', value)}
                      placeholder="12:00 PM"
                      className="time-dropdown"
                    />
                  </div>
                  
                  {/* Office Hours Days */}
                  <div className="days-selector">
                    {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'].map(day => (
                      <button
                        key={day}
                        type="button"
                        className={`day-button ${formData.officeHoursDays.includes(day) ? 'day-button-selected' : ''}`}
                        onClick={() => handleDayToggle('officeHoursDays', day)}
                      >
                        {formData.officeHoursDays.includes(day) && (
                          <svg className="check-icon"  viewBox="0 0 24 24" fill="none">
                            <path d="M9 12L11 14L15 10" stroke="#000000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        )}
                        <span>{day.slice(0, 3)}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="course-details-actions">
            <button className="course-details-cancel-btn" onClick={handleCancel}>
              Cancel
            </button>
            <button className="course-details-back-btn" onClick={handleBack}>
              Back
            </button>
            <button 
              className="course-details-confirm-btn" 
              onClick={handleAddCourseToProgram}
              disabled={isAddingCourse}
            >
              {isAddingCourse ? (
                <>
                  <div className="loading-spinner"></div>
                  Adding {type === 'module' ? 'Module' : 'Course'}...
                </>
              ) : (
                `Add ${type === 'module' ? 'Module' : 'Course'} to Program`
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseDetailsModal;
